import React, { useCallback, useMemo } from 'react';
import { useSimInventory } from './useSimInventory';
import { TableLayoutWithoutScrollArea } from 'src/common/components/Table/TableLayoutWithoutScrollArea';
import { useAtom, useAtomValue } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  simFiltersAtom,
  simSortingAtomWithStorage,
} from 'atoms/filters/sim';
import { Action } from 'src/common/components/ConfigurableTable/models/configurableTable';
import { useLocation, useNavigate } from 'react-router';
import { Routes } from 'src/Routes.constants';
import { SimTable } from 'src/common/components/Sim/Inventory/SimTable';
import { useSimProfilesConfigurationActions } from './ConfigurableTable/hooks/useSimProfilesConfigurationActions';
import { useSimProfilesColumnActions } from './ConfigurableTable/hooks/useSimProfilesColumnActions';
import { TABLE_VARIANT } from 'src/common/components/Sim/Inventory/constants/columnConfigurationsConstants';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { simInventoryQueries } from '../Inventory/queries/simInventoryQueries';
import {
  enrichWithSessionListDetailsMapper,
  useSessionsListDetails,
} from '../hooks/useSessionListDetails';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';

export const SimInventoryTable = React.memo(() => {
  const filters = useAtomValue(simFiltersAtom);
  const [sorting, setSorting] = useAtom(simSortingAtomWithStorage);

  const [rowsPerPage, setRowsPerPage] = useAtom(rowsPerPageFilterAtom);
  const [page, setPage] = useAtom(pageFilterAtom);
  const onPaginationChange = useCallback(
    (pagination: GridPaginationModel) => {
      if (pagination.page !== page) {
        setPage(pagination.page);
      }

      if (pagination.pageSize !== rowsPerPage) {
        setRowsPerPage(pagination.pageSize);
      }
    },
    [page, rowsPerPage, setPage, setRowsPerPage],
  );

  const {
    simInventory,
    isRefetching,
    pagination: { totalNumberOfItems },
  } = useSimInventory(filters, sorting, rowsPerPage, page);

  const imsis = simInventory?.items.filter((sim) => sim.imsi).map((sim) => sim.imsi as string);
  const isRadiusInterimsFilteringEnabled = useHasFeatureFlag('RadiusInterimsFiltering');

  const { data: sessionsListDetails } = useSessionsListDetails({
    imsis,
    queryKey: simInventoryQueries.sessionListDetails(imsis),
  });

  const mappedSimInventoryItems = simInventory?.items.map((item) =>
    isRadiusInterimsFilteringEnabled
      ? enrichWithSessionListDetailsMapper(item, sessionsListDetails)
      : item,
  );

  const { configuration } = simInventory;
  const { updateVariant, revertToDefault } = useSimProfilesConfigurationActions(configuration);
  const { flatColumnConfigurations, combinedColumnConfigurations, enabledVariant } = configuration;

  const { updateWidthOfColumn, reorder, updateVisibilityOfColumns } =
    useSimProfilesColumnActions(configuration);

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const [sortField] = model;
    if (!sortField) {
      return;
    }

    setSorting({
      columnName: sortField.field,
      sort: sortField.sort ?? null,
    });
  }, []);

  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const actions: Action[] = useMemo(
    () => [
      {
        id: 'viewDetails',
        label: 'View Details',
        type: 'custom',
        actionCallback: (data: any) => {
          navigate(`${Routes.simInventory}/${data.iccid}`, {
            state: {
              previousPath: `${pathname}${search}`,
            },
          });
        },
      },
    ],
    [],
  );

  return (
    <TableLayoutWithoutScrollArea
      fetching={isRefetching}
      table={
        <SimTable
          data-testid="sims inventory table"
          configuration={
            enabledVariant === TABLE_VARIANT.Combined
              ? combinedColumnConfigurations
              : flatColumnConfigurations
          }
          enabledVariant={enabledVariant}
          sort={sorting}
          onSortModelChange={onSortModelChange}
          updateWidthOfColumn={updateWidthOfColumn}
          reorder={reorder}
          updateVisibilityOfColumns={updateVisibilityOfColumns}
          updateVariant={updateVariant}
          revertToDefault={revertToDefault}
          rows={mappedSimInventoryItems}
          actions={actions}
          page={page}
          pageSize={rowsPerPage}
          rowCount={totalNumberOfItems}
          onPaginationChange={onPaginationChange}
        />
      }
    />
  );
});
